import { useState, useEffect } from 'react';

/**
 * Custom hook for managing reorderable columns
 * @param defaultColumns - The default columns configuration
 * @param storageKey - The localStorage key to use for saving column preferences
 * @returns An object with columns and column management functions
 */
export interface ColumnConfig {
    id: string;
    label: string;
    visible: boolean;
    order: number;
}

export const useColumnReorder = (
    defaultColumns: ColumnConfig[],
    storageKey: string
) => {
    // Load saved column preferences from localStorage or use defaults
    const loadSavedColumns = (): ColumnConfig[] => {
        try {
            const savedColumns = localStorage.getItem(storageKey);
            if (savedColumns) {
                const parsed = JSON.parse(savedColumns);
                // Merge saved preferences with default columns to handle any new columns added later
                const mergedColumns = defaultColumns.map(col => {
                    const savedCol = parsed.find(c => c.id === col.id);
                    return savedCol
                        ? {
                              ...col,
                              visible: savedCol.visible,
                              order:
                                  savedCol.order !== undefined
                                      ? savedCol.order
                                      : col.order,
                          }
                        : col;
                });
                // Sort columns by order
                return mergedColumns.sort((a, b) => a.order - b.order);
            }
        } catch (e) {
            console.error(
                `Error loading column preferences for ${storageKey}`,
                e
            );
        }
        return [...defaultColumns].sort((a, b) => a.order - b.order);
    };

    const [columns, setColumns] = useState<ColumnConfig[]>(loadSavedColumns());

    // Save column preferences to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem(storageKey, JSON.stringify(columns));
    }, [columns, storageKey]);

    // Toggle column visibility
    const handleColumnToggle = (columnId: string) => {
        setColumns(prevColumns =>
            prevColumns.map(col =>
                col.id === columnId ? { ...col, visible: !col.visible } : col
            )
        );
    };

    // Select all columns
    const handleSelectAll = () => {
        setColumns(prevColumns =>
            prevColumns.map(col => ({ ...col, visible: true }))
        );
    };

    // Deselect all columns except for a required one
    const handleDeselectAll = (requiredColumnId: string = 'id') => {
        setColumns(prevColumns =>
            prevColumns.map(col => ({
                ...col,
                visible: col.id === requiredColumnId,
            }))
        );
    };

    // Handle column reordering
    const handleColumnReorder = (
        sourceIndex: number,
        destinationIndex: number
    ) => {
        setColumns(prevColumns => {
            const result = Array.from(prevColumns);
            const [removed] = result.splice(sourceIndex, 1);
            result.splice(destinationIndex, 0, removed);

            // Update order values for all columns
            return result.map((col, index) => ({
                ...col,
                order: index,
            }));
        });
    };

    // Check if a column is visible
    const isColumnVisible = (columnId: string) => {
        const column = columns.find(col => col.id === columnId);
        return column ? column.visible : true;
    };

    return {
        columns,
        isColumnVisible,
        handleColumnToggle,
        handleSelectAll,
        handleDeselectAll,
        handleColumnReorder,
    };
};

export default useColumnReorder;
